import { must } from "@corpuscomplete/core/utils";

/**
 * Generic versioned registry for storing and retrieving versioned items.
 *
 * Used by workflow and agent registries to provide consistent version management.
 */

export type VersionedRegistryConfig<T> = {
  getType: (item: T) => string;
  getVersion: (item: T) => number;
  entityName: string;
};

export type VersionedRegistry<T> = {
  register: (item: T) => void;
  get: (type: string, version?: number) => T;
  getLatestVersion: (type: string) => number;
  hasVersion: (type: string, version: number) => boolean;
  getVersions: (type: string) => number[];
};

type VersionedStorage<T> = {
  versions: Map<string, Map<number, T>>;
  latest: Map<string, number>;
};

export function createVersionedRegistry<T>(
  config: VersionedRegistryConfig<T>,
  initialItems?: T[],
): VersionedRegistry<T> {
  const { getType, getVersion, entityName } = config;

  const storage: VersionedStorage<T> = {
    versions: new Map(),
    latest: new Map(),
  };

  const register = (item: T) => {
    const type = getType(item);
    const version = getVersion(item);

    if (!storage.versions.has(type)) {
      storage.versions.set(type, new Map());
    }

    const itemVersions = must(storage.versions.get(type));
    itemVersions.set(version, item);

    const currentLatest = storage.latest.get(type);
    if (currentLatest === undefined || version >= currentLatest) {
      storage.latest.set(type, version);
    }
  };

  if (initialItems) {
    for (const item of initialItems) {
      register(item);
    }
  }

  return {
    register,

    get: (type, version?) => {
      const itemVersions = storage.versions.get(type);
      if (!itemVersions || itemVersions.size === 0) {
        throw new Error(`${entityName} type "${type}" not found in registry`);
      }

      if (version !== undefined) {
        const item = itemVersions.get(version);
        if (!item) {
          throw new Error(
            `${entityName} type "${type}" version "${version}" not found in registry. ` +
              `Available versions: ${Array.from(itemVersions.keys()).join(", ")}`,
          );
        }
        return item;
      }

      const latestVersion = must(storage.latest.get(type));
      return must(itemVersions.get(latestVersion));
    },

    getLatestVersion: (type) => {
      const latestVersion = storage.latest.get(type);
      if (latestVersion === undefined) {
        throw new Error(`${entityName} type "${type}" not found in registry`);
      }
      return latestVersion;
    },

    hasVersion: (type, version) => {
      const itemVersions = storage.versions.get(type);
      return itemVersions?.has(version) ?? false;
    },

    getVersions: (type) => {
      const itemVersions = storage.versions.get(type);
      if (!itemVersions) {
        return [];
      }
      return Array.from(itemVersions.keys()).sort((a, b) => b - a);
    },
  };
}
